import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  addTradedFacilityFee,
  deleteTradedFacilityFee,
  getTradedFacilities,
  startLoadingTradedFacilities,
  stopLoadingTradedFacilities,
  updateTradedFacilityFee,
} from './actions'
import { loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks'
import moment from 'moment'
import { coalesceThunk } from 'app/state/utils/coalesce-thunk'

const getAllPositions = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/traded-facilities`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadTradedFacilities = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingTradedFacilities())
      const allPositions = await getAllPositions()
      return dispatch(getTradedFacilities(allPositions))
    } catch (e) {
      console.log(e)
      return []
    } finally {
      dispatch(stopLoadingTradedFacilities())
    }
  },
)

export const applyCurrentAmountChange = (body: any) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/traded-facilities/apply-changes`, body)
    .then(payload => {
      return { success: true, payload: payload.data }
    })
    .catch(e => {
      console.log(e)
      return { success: false, payload: e.response.data.error }
    })
}

export const editValue = (body: any) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/traded-facilities/edit-value`, body)
    .then(payload => {
      return { success: true, payload: payload.data }
    })
    .catch(e => {
      console.log(e)
      return { success: false, payload: e.response.data.error }
    })
}

export const addSubTradedFacilityFee =
  (body: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/traded-facilities/fee`, body)
      .then(payload => {
        dispatch(addTradedFacilityFee(payload.data))
        return { success: true, payload: payload.data }
      })
      .catch(e => {
        console.log(e)
        return { success: false, payload: e.response.data.error }
      })
  }

export const editSubTradedFacilityFee =
  (body: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .put(`${apiServerUrl}/api/traded-facilities/fee`, body)
      .then(payload => {
        dispatch(updateTradedFacilityFee(payload.data))
        return { success: true, payload: payload.data }
      })
      .catch(e => {
        console.log(e)
        return { success: false, payload: e.response.data.error }
      })
  }

export const overrideSubTradedFacilityFee =
  (body: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/traded-facilities/fee/override`, body)
      .then(async payload => {
        dispatch(updateTradedFacilityFee(payload.data))
        // Ensures that after overriding a fee, the SubTrade panel reflects the 
        // updated fee amount without needing a manual refresh.
        const subTradeId = body?.subTradeKey?.id
        if (subTradeId) {
          await (loadFMMetadataForSubTrade(subTradeId) as any)(dispatch)
        }
        return { success: true, payload: payload.data }
      })
      .catch(e => {
        console.log(e)
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteSubTradeFacilityFees =
  (facilityId: string, subTradeId: string, feeType: string) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(
        `${apiServerUrl}/api/traded-facilities/fee?facilityId=${facilityId}&type=${feeType}&subTradeId=${subTradeId}`,
      )
      .then(() => {
        dispatch(deleteTradedFacilityFee({ facilityId, subTradeId, feeType }))
        return { success: true, payload: { facilityId, subTradeId, feeType } }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const removeOverriddenSubTradedFacilityFee =
  (facilityId: string, subTradeId: string, feeType: string) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(
        `${apiServerUrl}/api/traded-facilities/fee/override?facilityId=${facilityId}&type=${feeType}&subTradeId=${subTradeId}`,
      )
      .then(async payload => {
        dispatch(updateTradedFacilityFee(payload.data))
        // Refresh FM metadata so the SubTrade panel reverts to the calculated
        // amount without a page reload.
        await (loadFMMetadataForSubTrade(subTradeId) as any)(dispatch)
        return { success: true, payload: payload.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const calculateFee = (facilityId: string, subTradeId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/traded-facilities/fee/calculate?facilityId=${facilityId}&subTradeId=${subTradeId}`,
    )
    .then(payload => {
      return payload.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const calculateFeeByDays = (facilityId: string, subTradeId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/traded-facilities/fee/calculate-by-days?facilityId=${facilityId}&subTradeId=${subTradeId}`,
    )
    .then(payload => {
      return payload.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const calculateAverageSofr = (subTradeId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/traded-facilities/calculate-average-sofr?subTradeId=${subTradeId}`,
    )
    .then(payload => {
      return payload.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

export const calculateFMFeeBreakdown = (
  subTradeId: string,
  facilityId: string,
  effectiveDate: string,
) => {
  const apiServerUrl = config.backendUrl
  const effDate = moment(effectiveDate).format('YYYY-MM-DD')
  return client
    .get(
      `${apiServerUrl}/api/traded-facilities/fee/calculate-fm-fee-breakdown?subTradeId=${subTradeId}&facilityId=${facilityId}&effectiveDate=${effDate}`,
    )
    .then(payload => {
      return payload.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}
